import { Link, useNavigate } from "react-router-dom";
import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { Toast, ToastDescription, ToastTitle, ToastViewport } from "./ui/toast";
import { ShoppingCart } from "lucide-react";
import { useUserType } from "../context/UserTypeContext";

const MobileMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { userSession, logout } = useAuth(); // Get session and logout from context
  const [toastList, setToastList] = useState([]); // Manage toasts
  const [cartCount, setCartCount] = useState(0); // State to manage cart count

  const { setUserType } = useUserType();

  const role = userSession?.role;

  const handleAddToCart = () => {
    // Mock function to simulate adding items to the cart
    setCartCount((prevCount) => prevCount + 1);
  };

  const handlePdfClick = (url) => {
    window.open(url, "_blank");
    setIsOpen((prev) => !prev);
  };

  const handleMenuClick = () => {
    setIsOpen((prev) => !prev);
  };

  const handleNavigation = (path) => {
    setIsOpen(false); // Close menu before navigating
    navigate(path);
  };

  const handleClick = (requiredUserType, path) => {
    setIsOpen((prev) => !prev);

    if (userSession?.userType !== requiredUserType) {
      const message =
        requiredUserType === "applicator"
          ? "You need to Sign in as an APPLICATOR"
          : "You need to Sign in as a CUSTOMER";

      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "Access Denied",
          description: message,
          variant: "destructive",
        },
      ]);
      return;
    }
    navigate(path);
  };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <section>
      <div className="md:tw-hidden tw-flex tw-items-center tw-gap-4">
        <div
          className="tw-flex tw-flex-col tw-gap-[7px] tw-cursor-pointer"
          onClick={handleMenuClick}
        >
          <div
            className={`tw-w-[40px] tw-h-[6px] tw-bg-[#c1c1c1] tw-rounded-[2px] tw-transition-all tw-ease-in-out tw-duration-500 ${
              isOpen ? "tw-rotate-45 tw-translate-y-[19px]" : ""
            }`}
          />
          <div
            className={`tw-w-[40px] tw-h-[6px] tw-bg-[#c1c1c1] tw-rounded-[2px] tw-transition-opacity tw-duration-300 ${
              isOpen ? "tw-opacity-0" : "tw-opacity-100"
            }`}
          />
          <div
            className={`tw-w-[40px] tw-h-[6px] tw-bg-[#c1c1c1] tw-rounded-[2px] tw-transition-all tw-ease-in-out tw-duration-500 ${
              isOpen ? "tw--rotate-45 tw-translate-y-[-7px]" : ""
            }`}
          />
        </div>
      </div>
      {isOpen && (
        <div className="tw-absolute tw-top-[80px] tw-left-0 tw-w-full tw-h-[calc(100vh-80px)] tw-items-center tw-justify-center tw-gap-6 tw-z-50 tw-ease-in tw-duration-200 tw-bg-[#1e1e1e] tw-flex tw-flex-col">
          <div className="tw-flex tw-flex-col  md:tw-hidden tw-space-y-4 tw-items-center tw-justify-center">
            <Button
              variant="mobilemenu"
              onClick={() => handleNavigation("/")}
              className="tw-text-base"
            >
              Home
            </Button>

            {/* Portals Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="mobilemenu" className="tw-text-base">
                  Portals
                </Button>
              </PopoverTrigger>
              <PopoverContent className="tw-w-auto tw-text-center">
                <button
                  onClick={() => handleClick("applicator", "/projectlist")}
                  className="tw-block tw-w-40 tw-text-center tw-py-2 tw-text-black hover:tw-text-[#00B5ED] hover:tw-bg-transparent tw-bg-transparent focus:tw-bg-transparent tw-px-6"
                >
                  Applicator
                </button>
                <button
                  onClick={() => handleClick("customer", "/projectinfo")}
                  className="tw-block tw-w-40 tw-text-center tw-py-2 tw-text-black hover:tw-text-[#00B5ED] hover:tw-bg-transparent tw-bg-transparent focus:tw-bg-transparent tw-px-6"
                >
                  Customer
                </button>
              </PopoverContent>
            </Popover>

            {/* Product Information Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="mobilemenu" className="tw-text-base">
                  Product Information
                </Button>
              </PopoverTrigger>
              <PopoverContent className="tw-w-auto tw-text-center tw-flex tw-flex-col tw-gap-1">
                {/* Resiline 320 */}
                <span className="tw-text-[#00B5ED] tw-font-semibold">
                  Resiline 320
                </span>
                <button
                  onClick={() => handlePdfClick("/r320use.pdf")}
                  className="tw-text-black hover:tw-text-[#00B5ED]"
                >
                  Resiline Use.pdf
                </button>
                <button
                  onClick={() => handlePdfClick("/r320guidance.pdf")}
                  className="tw-text-black hover:tw-text-[#00B5ED]"
                >
                  Resiline Guidance.pdf
                </button>
                <button
                  onClick={() => handlePdfClick("/nsfcertificate.pdf")}
                  className="tw-text-black hover:tw-text-[#00B5ED]"
                >
                  NSF Certificate
                </button>
                <button
                  onClick={() => handlePdfClick("/resimac.pdf")}
                  className="tw-text-black hover:tw-text-[#00B5ED]"
                >
                  PFAS (Resimac)
                </button>
                <button
                  onClick={() => handlePdfClick("/sdsactivator.pdf")}
                  className="tw-text-black hover:tw-text-[#00B5ED]"
                >
                  SDS Activator
                </button>
                <button
                  onClick={() => handlePdfClick("/sdsbase.pdf")}
                  className="tw-text-black hover:tw-text-[#00B5ED]"
                >
                  SDS Base
                </button>
                <button
                  onClick={() => handlePdfClick("/r320tds.pdf")}
                  className="tw-text-black hover:tw-text-[#00B5ED]"
                >
                  Technical Data Sheet
                </button>
                <button
                  onClick={() => handlePdfClick("/r320testreport.pdf")}
                  className="tw-text-black hover:tw-text-[#00B5ED]"
                >
                  Test Report
                </button>

                {/* Resiline 330 */}
                <span className="tw-mt-4 tw-text-[#00B5ED] tw-font-semibold">
                  Resiline 330
                </span>
                <button
                  onClick={() => handlePdfClick("/r330casestudy.pdf")}
                  className="tw-text-black hover:tw-text-[#00B5ED]"
                >
                  Case Study.pdf
                </button>
                <button
                  onClick={() => handlePdfClick("/r330ifu.pdf")}
                  className="tw-text-black hover:tw-text-[#00B5ED]"
                >
                  IFU Issue.pdf
                </button>
                <button
                  onClick={() => handlePdfClick("/r330tds.pdf")}
                  className="tw-text-black hover:tw-text-[#00B5ED]"
                >
                  TDS Issue.pdf
                </button>

                {/* Resiline 325 */}
                <span className="tw-mt-4 tw-text-[#00B5ED] tw-font-semibold">
                  Resiline 325
                </span>
                <span className="tw-text-gray-500 tw-text-sm">Coming Soon</span>
              </PopoverContent>
            </Popover>

            {/* Resources Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="mobilemenu" className="tw-text-base">
                  Resources
                </Button>
              </PopoverTrigger>
              <PopoverContent className="tw-w-auto tw-text-center">
                <button
                  onClick={() => handleNavigation("/")}
                  className="tw-block tw-w-40 tw-py-2 tw-text-black hover:tw-text-[#00B5ED]"
                >
                  Marketing Material
                </button>
                <button
                  onClick={() => handleNavigation("/")}
                  className="tw-block tw-w-40 tw-py-2 tw-text-black hover:tw-text-[#00B5ED]"
                >
                  Specifications
                </button>
                <button
                  onClick={() => handleNavigation("/")}
                  className="tw-block tw-w-40 tw-py-2 tw-text-black hover:tw-text-[#00B5ED]"
                >
                  Case Studies
                </button>
                <button
                  onClick={() => handleNavigation("/")}
                  className="tw-block tw-w-40 tw-py-2 tw-text-black hover:tw-text-[#00B5ED]"
                >
                  Equipment Information
                </button>
              </PopoverContent>
            </Popover>
          </div>

          {/* Cart Icon */}
          <button
            onClick={() => handleNavigation("/checkout")}
            className="tw-relative tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-bg-transparent tw-border-none tw-cursor-pointer"
            aria-label="Cart"
          >
            <ShoppingCart className="tw-text-white" size={24} />
            {cartCount > 0 && (
              <span className="tw-absolute tw-top-0 tw-right-0 tw-bg-[#00B5ED] tw-text-white tw-rounded-full tw-text-xs tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center">
                {cartCount}
              </span>
            )}
          </button>

          {/* Add to Cart Button for Testing */}
          <Button
            variant="darkbg"
            className="tw-hidden" // Hide in production
            onClick={handleAddToCart}
          >
            Add to Cart (Test)
          </Button>

          {/* Admin Button (Only for Admin & Superadmin) */}
          {(role === "admin" || role === "superadmin") && (
            <Button
              className="tw-bg-[#00B5ED] tw-text-white tw-font-medium tw-px-4 tw-py-2 tw-rounded-md"
              onClick={() => {
                navigate("/admin");
                handleMenuClick();
              }}
            >
              Admin
            </Button>
          )}

          {/* <div className="tw-border-b tw-border-white tw-w-3/4" /> */}

          {userSession ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-space-y-4">
              <span className="tw-text-white">
                Welcome,{" "}
                <span className="tw-font-medium">{userSession.firstName}</span>
              </span>
              <Button
                variant="destructive"
                className="tw-text-white hover:tw-bg-destructive/80 tw-font-medium tw-px-4 tw-py-2 tw-rounded-md"
                onClick={() => {
                  logout();
                  handleMenuClick();
                }}
                aria-label="Log Out"
              >
                Log Out
              </Button>
            </div>
          ) : (
            <>
              <Link to="/signin">
                <Button
                  onClick={() => {
                    setUserType("applicator");
                    handleMenuClick();
                  }}
                  variant="mobilemenu"
                  className="tw-text-base"
                >
                  Applicator Sign in
                </Button>
              </Link>

              {/* <Link to="/signin">
                <Button
                  onClick={() => {
                    setUserType("customer");
                    handleMenuClick();
                  }}
                  variant="mobilemenu"
                  className="tw-text-base"
                >
                  Non-Applicator Sign in
                </Button>
              </Link> */}
            </>
          )}

          <Link to="/contactus">
            <Button
              onClick={() => {
                handleMenuClick();
              }}
              variant="mobilemenu"
              className="tw-text-base"
            >
              Contact Us
            </Button>
          </Link>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </section>
  );
};

export default MobileMenu;
